import React from 'react'
import { Helmet } from 'react-helmet-async'
import type { HelmetProps } from 'react-helmet'
import { relativeToAbsoluteUrl } from '#src/services/navigation'
import config from '#src/config'

type TwitterCardType = 'summary' | 'summary_large_image' | 'app' | 'player'

interface PageMetadataProps extends HelmetProps {
  // The title to use in the browser bar and in Open Graph metadata.
  title?: string
  // By default "| Curated" is appended to the title in the browser bar. Use this prop to disable that behavior.
  ignoreTitleTemplate?: boolean
  // Used as the meta description and og:description.
  description?: string
  // The canonical path of this page.
  canonicalPath?: string
  // The open graph image.
  imageUrl?: string
  // The type of this page. Defaults to "website" but can be set to something else for more specific use-cases, e.g. "article".
  type?: string
  // How Twitter should render links to this page.
  twitterCardType?: TwitterCardType
}

const PageMetadata: React.FC<PageMetadataProps> = ({
  title,
  ignoreTitleTemplate,
  description,
  canonicalPath,
  imageUrl,
  type,
  twitterCardType,
  ...helmetProps
}) => {
  const canonicalUrl = canonicalPath && relativeToAbsoluteUrl(canonicalPath)
  const env: string = config.get('environment')
  const envTag = env === 'staging' ? '[STAGING] ' : env.startsWith('dev') ? '[DEV] ' : ''

  return (
    <Helmet
      defaultTitle={`${envTag}Curated`}
      titleTemplate={ignoreTitleTemplate ? undefined : '%s | Curated.com'}
      {...helmetProps}
    >
      {description && <meta name="description" content={description} />}
      {description && <meta property="og:description" content={description} />}
      {canonicalPath && <link rel="canonical" href={canonicalUrl} />}
      {canonicalPath && <meta property="og:url" content={canonicalUrl} />}

      {title && <title>{`${envTag}${title}`}</title>}
      {title && <meta property="og:title" content={title} />}
      {imageUrl && <meta property="og:image" content={imageUrl} />}
      {type && <meta property="og:type" content={type} />}
      {twitterCardType && <meta property="twitter:card" content={twitterCardType} />}
    </Helmet>
  )
}

export default PageMetadata
